import { type ReactNode } from 'react';
import { Select } from '@/components/ui/select';

const LABEL_CLS = 'mb-1.5 block text-[11px] text-text-tertiary';
const NUM_CLS =
  'w-full rounded-[10px] border border-border-subtle bg-transparent px-3 text-xs text-text-primary h-[34px] transition-colors hover:border-border-default focus:border-brand-red focus:outline-none focus:ring-2 focus:ring-brand-red/30';
const UNIT_CLS =
  'pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-text-tertiary';

type NumProps = {
  label: string;
  value: number;
  onChange: (v: number) => void;
  tip?: string;
  unit?: string;
};

function UnitSuffix({ unit }: { unit?: string | undefined }): ReactNode {
  if (!unit) return null;
  return <span className={UNIT_CLS}>{unit}</span>;
}

export function NumField({
  label,
  value,
  onChange,
  tip,
  unit,
}: NumProps): ReactNode {
  return (
    <div>
      <label className={LABEL_CLS} title={tip}>
        {label}
      </label>
      <div className="relative">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(+e.target.value)}
          className={NUM_CLS}
          style={unit ? { paddingRight: '4rem' } : undefined}
        />
        <UnitSuffix unit={unit} />
      </div>
    </div>
  );
}

type ToggleProps = {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
};

export function ToggleField({
  label,
  value,
  onChange,
}: ToggleProps): ReactNode {
  const track = value ? 'bg-brand-red' : 'bg-white/[0.1]';
  const knob = value ? 'translate-x-5' : 'translate-x-0';
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-text-tertiary">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative h-6 w-11 rounded-full transition-colors ${track}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${knob}`}
        />
      </button>
    </div>
  );
}

type SelProps = {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  options: { value: string | number; label: string }[];
};

export function SelField({
  label,
  value,
  onChange,
  options,
}: SelProps): ReactNode {
  return (
    <div>
      <span className={LABEL_CLS}>{label}</span>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="!h-[34px] !text-xs"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </div>
  );
}
